import { useCurrentUserId } from '@framework/auth';
import { Redirect } from 'react-router-dom';
import { UserPagePathBuilder } from '../UserPagePathBuilder';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@framework/ui/atoms/Button';
import { Header } from './Header';
import { StopMailRequestConfirmModal } from './StopMailRequestConfirmModal';
import { FunctionsUserActions } from '@functions/FunctionsUserActions';

const DistributionStatus = {
  distributing: '配信中',
  stopped: '配信停止済み',
  willBeFinishedToday: '本日最終配信',
  finished: '配信終了',
} as const;

/**
 * メール通知停止設定画面
 *
 * @returns
 */
export const StopMailRequestPage: React.FC<unknown> = () => {
  const [isConfirmOpen, setConfirmOpen] = useState<boolean>(false);
  const [status, setStatus] = useState('-');
  const [stoppable, setStoppable] = useState<boolean>(false);

  const currentUserId = useCurrentUserId();

  const handleCloseConfirm = useCallback(() => {
    setConfirmOpen(false);
  }, []);

  const handleOnboardingStop = useCallback(() => {
    setConfirmOpen(true);
  }, []);

  const stopOnboardingMail = useCallback(async () => {
    if (currentUserId) {
      await FunctionsUserActions.stopOnboardingGuide(currentUserId);
      location.reload();
    }
  }, [currentUserId]);

  const kind = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('kind') || '';
  }, []);

  useEffect(() => {
    async function fetch() {
      if (currentUserId) {
        const response = await FunctionsUserActions.fetchOnboardingGuideDistributionStatus(currentUserId);
        if (response) {
          setStatus(DistributionStatus[response.status]);
          setStoppable(response.stoppable);
        }
      }
    }
    fetch();
  }, [currentUserId]);

  if (!currentUserId) {
    // ログインは必須（ログイン後に本画面を表示）
    const requestPath = location.pathname + location.search;
    return <Redirect to={UserPagePathBuilder.loginPage(requestPath)} />;
  }

  return (
    <>
      <Header currentUserId={currentUserId} />
      {kind === 'onboarding-guide' && (
        <>
          <Contents onOnboardingStop={handleOnboardingStop} status={status} stoppable={stoppable} />
          <StopMailRequestConfirmModal
            isOpen={isConfirmOpen}
            onSubmit={stopOnboardingMail}
            onClose={handleCloseConfirm}
          />
        </>
      )}
    </>
  );
};

type ContentsProps = {
  onOnboardingStop(): void;
  status: string;
  stoppable: boolean;
};

const Contents: React.FC<ContentsProps> = ({ onOnboardingStop, status, stoppable }: ContentsProps) => {
  return (
    <div className="p-8">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-bold">メール配信設定</h2>
      </div>
      <div className="overflow-y-scroll pb-8" style={{ maxHeight: 'calc(100vh - 138px)' }}>
        <table className="w-full border-separate">
          <thead className="sticky top-0 z-10 bg-white">
            <tr className="text-left">
              <th className="border-y-2 p-2">種類</th>
              <th className="border-y-2 p-2">状態</th>
              <th className="border-y-2 p-2" />
            </tr>
          </thead>
          <tbody>
            <tr className="border-b-2">
              <td className="border-b-2 border-gray-400 py-4 align-middle">オンボーディング</td>
              <td className="border-b-2 border-gray-400 py-4 align-middle">{status}</td>
              <td className="border-b-2 border-gray-400 py-4 align-middle">
                <Button size="sm" color="brand" onClick={onOnboardingStop} disabled={!stoppable}>
                  配信停止
                </Button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};
